// SINGLE SOURCE OF TRUTH for BuildEase plan pricing (approved V1 model).
// Cards, comparison table and FAQ all read from this file so they cannot drift apart.
// Prices are integers; render through formatRand only.

export const formatRand = (n) => `R${n.toLocaleString("en-US")}`;

export const PRICE_NOTE = {
    free: "No payment",
    paid: "Once-off · per project",
};

export const STATUS_LABELS = {
    launch: "AT LAUNCH",
    dev: "IN DEVELOPMENT",
    roadmap: "ROADMAP",
};

export const PHASED_NOTE =
    "Feature availability is phased. Plans include future updates as they are released.";

export const FINE_PRINT = [
    {
        k: "EXCLUSIONS",
        v: "Each plan includes the features listed on its card. Anything not listed is not included — feature scope is published per plan so nothing is implied.",
    },
    {
        k: "ACCESS DURATION",
        v: "Free projects remain available while your account is active. Paid projects include lifetime access, with free updates within that plan as they are released.",
    },
    {
        k: "UPGRADES & CANCELLATION",
        v: "Upgrades from a lower plan happen at launch by paying the difference — there is no recurring billing to cancel. Payments are once-off, processed by PayFast; BuildEase never stores card details.",
    },
];

export const VAT_NOTE = "[VAT_NOTE]";

export const TERMS_URL = "[TERMS_URL]";
export const REFUND_POLICY_URL = "[REFUND_POLICY_URL]";

export const AI_NOTE =
    "BuildEase is AI and can make mistakes. Verify important information before making construction, financial, legal, safety or other significant decisions. Estimates are not guarantees.";

export const HOW_PRICING_WORKS = [
    {
        code: "P.01",
        title: "One project, one plan",
        copy: "One account. Multiple projects. Each home has its own BuildEase plan — a plan belongs to a project, not an account.",
    },
    {
        code: "P.02",
        title: "Lifetime access to your project",
        copy: "A paid project keeps lifetime access, with free updates within that plan as they are released.",
    },
    {
        code: "P.03",
        title: "Revised plans are never charged again",
        copy: "Revised plans, extra drawings and amendments for the same project never trigger a new charge. A genuinely different property needs its own project.",
    },
];

export const PLANS = [
    {
        id: "free",
        name: "FREE",
        price: 0,
        priceNote: PRICE_NOTE.free,
        recommended: false,
        features: [
            { label: "1 project, 1 plan upload", status: "launch" },
            {
                label: "Plan summary: rooms, levels, approximate footprint",
                status: "launch",
            },
            { label: "Project overview, status only", status: "launch" },
            { label: "1 Quote Analyser run, lifetime", status: "launch" },
            { label: "5 one-off chat credits", status: "launch" },
            {
                label: "Preview of top 3 compliance considerations",
                status: "dev",
            },
            { label: "Help centre support" },
        ],
    },
    {
        id: "standard",
        name: "STANDARD",
        price: 1499,
        priceNote: PRICE_NOTE.paid,
        recommended: true,
        features: [
            { label: "Everything in Free" },
            {
                label: "Unlimited same-project documents, up to 5 plan interpretation runs",
                status: "launch",
            },
            {
                label: "Full plan extraction, with ambiguities flagged",
                status: "launch",
            },
            {
                label: "BOQ and cost estimate range, labelled Verified / Estimated / Assumed",
                status: "launch",
            },
            {
                label: "Location-based materials pricing where verified data exists",
                status: "launch",
            },
            {
                label: "3 Quote Analyser runs with line-item comparison",
                status: "launch",
            },
            {
                label: '50 chat credits plus "Explain this to me"',
                status: "launch",
            },
            {
                label: "Project dashboard and Decision Register",
                status: "dev",
            },
            { label: "Stage-based compliance checklist", status: "dev" },
            { label: "Home Vision: 15 exterior concepts", status: "roadmap" },
            {
                label: "1 GB storage, PDF export, shareable read-only report, email support",
            },
        ],
    },
    {
        id: "premium",
        name: "PREMIUM",
        price: 3499,
        priceNote: PRICE_NOTE.paid,
        recommended: false,
        features: [
            { label: "Everything in Standard" },
            {
                label: "Plan revision comparison and cross-drawing conflict detection",
                status: "launch",
            },
            { label: "Up to 15 plan interpretation runs", status: "launch" },
            {
                label: "10 Quote Analyser runs per project, fair use",
                status: "launch",
            },
            { label: "200 chat credits", status: "launch" },
            { label: "Excel and CSV export", status: "launch" },
            { label: "Priority support", status: "launch" },
            {
                label: "Budget Protection (target vs estimated vs committed cost)",
                status: "dev",
            },
            {
                label: "Quote clarification questions and negotiation workspace",
                status: "dev",
            },
            {
                label: "Construction readiness and Change Impact trackers",
                status: "dev",
            },
            { label: "Evidence Vault, 5 GB", status: "dev" },
            {
                label: "Home Vision: 40 generations, interiors, hero renders, A/B compare",
                status: "roadmap",
            },
            {
                label: "Design Alternatives, Material Alternatives, Value Engineering",
                status: "roadmap",
            },
            {
                label: "Green and energy intelligence, project-site weather",
                status: "roadmap",
            },
            {
                label: "Invite up to 3 professionals to review the project",
                status: "roadmap",
            },
        ],
    },
];

export const COMPARISON = [
    {
        label: "Plan uploads & interpretation runs",
        free: "1 upload",
        standard: "Up to 5 runs",
        premium: "Up to 15 runs",
    },
    {
        label: "Full plan extraction, ambiguities flagged",
        free: "—",
        standard: "Included",
        premium: "Included",
    },
    {
        label: "BOQ & cost estimate range",
        free: "—",
        standard: "Included",
        premium: "Included",
    },
    {
        label: "Location-based materials pricing",
        free: "—",
        standard: "Included",
        premium: "Included",
    },
    {
        label: "Quote Analyser runs",
        free: "1, lifetime",
        standard: "3",
        premium: "10, fair use",
    },
    {
        label: "Chat credits",
        free: "5 one-off",
        standard: '50 + "Explain this to me"',
        premium: "200",
    },
    {
        label: "Project dashboard & Decision Register",
        free: "Overview, status only",
        standard: "dev",
        premium: "dev",
    },
    {
        label: "Compliance",
        free: "Top 3 preview (dev)",
        standard: "Stage-based checklist (dev)",
        premium: "Stage-based checklist (dev)",
    },
    {
        label: "Home Vision",
        free: "—",
        standard: "15 exterior concepts (roadmap)",
        premium: "40 generations, interiors, A/B (roadmap)",
    },
    {
        label: "Budget Protection & change trackers",
        free: "—",
        standard: "—",
        premium: "dev",
    },
    {
        label: "Design & material alternatives, value engineering",
        free: "—",
        standard: "—",
        premium: "roadmap",
    },
    {
        label: "Green & energy intelligence, site weather",
        free: "—",
        standard: "—",
        premium: "roadmap",
    },
    {
        label: "Professional review invites",
        free: "—",
        standard: "—",
        premium: "Up to 3",
    },
    {
        label: "Storage",
        free: "—",
        standard: "1 GB",
        premium: "5 GB",
    },
    {
        label: "Exports",
        free: "—",
        standard: "PDF + read-only report",
        premium: "Excel & CSV",
    },
    {
        label: "Support",
        free: "Help centre",
        standard: "Email",
        premium: "Priority",
    },
];

export const FAQS = [
    {
        q: "What counts as a project?",
        a: "One independent property or building.",
    },
    {
        q: "Do I pay monthly?",
        a: "No. It is a once-off payment per project.",
    },
    {
        q: "I'm building a second property.",
        a: "Each property is its own project with its own plan.",
    },
    {
        q: "What if my plans are revised?",
        a: "Same project, no new charge.",
    },
    {
        q: "How do I pay?",
        a: "Via PayFast, using card, Instant EFT and other SA methods. BuildEase never stores card details.",
    },
    {
        q: "Is everything available now?",
        a: "Features are released in phases; each is labelled.",
    },
    {
        q: "What about professionals?",
        a: "BuildEase Pro is V2; pricing will be announced closer to launch.",
    },
    {
        q: "Cooling-off and refunds?",
        a: "",
        refund: true,
    },
];
